// Geometry helpers shared by snapping, clamping, overlap detection and the
// drag handler. Keep this a leaf: it imports nothing from the planner beyond
// ObjectNameMap and types, which is what lets the snapping modules use it
// without importing cabinetPosition.ts back.
import * as THREE from 'three';
import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import ObjectNameMap from 'components/customer/RoomPlanner/constants/ObjectNameMap';

/**
 * `toggleDoor` exists on every operation mesh; only door/drawer ops set
 * `boundsExcludeAnimatedDoorOrDrawerPart` in attachAnimations (see Preview3DCommon/animations/handler).
 *
 * @param {THREE.Object3D} node Object under the cabinet (e.g. a mesh)
 * @param {THREE.Object3D} cabinetRoot Cabinet group root; walk stops here
 * @param {boolean} includeDrawerFaces Product include_drawer_faces flag
 * @return {boolean} True if under an animated door/drawer operation root
 */
const isMeshUnderAnimatedDoorOrDrawerPart = (
    node: THREE.Object3D,
    cabinetRoot: THREE.Object3D,
    includeDrawerFaces: boolean
): boolean => {
    if (!includeDrawerFaces) return false;
    let current: THREE.Object3D | null = node;
    while (current && current !== cabinetRoot) {
        const ud = current.userData as {
            boundsExcludeAnimatedDoorOrDrawerPart?: unknown;
        };
        if (ud.boundsExcludeAnimatedDoorOrDrawerPart === true) return true;
        current = current.parent;
    }
    return false;
};

export const computeWorldBoundsExcludingAnimatedDoorParts = (
    cabinetRoot: THREE.Group<THREE.Object3DEventMap>
    /** Set true temporarily to log which meshes are skipped as door/drawer faces. */
): THREE.Box3 => {
    const values = cabinetRoot.userData?.values as
        {include_drawer_faces?: boolean} | undefined;
    const includeDrawerFaces =
        typeof values?.include_drawer_faces !== 'undefined'
            ? Boolean(values.include_drawer_faces)
            : true;

    const combinedBounds = new THREE.Box3();
    combinedBounds.makeEmpty();

    const meshBounds = new THREE.Box3();
    cabinetRoot.updateWorldMatrix(true, true);

    cabinetRoot.traverse((child) => {
        if (!child.visible || child === cabinetRoot) return;
        if (
            isMeshUnderAnimatedDoorOrDrawerPart(
                child,
                cabinetRoot,
                includeDrawerFaces
            )
        ) {
            return;
        }
        if (!(child as THREE.Mesh).isMesh) return;

        meshBounds.setFromObject(child);
        if (!meshBounds.isEmpty()) {
            combinedBounds.union(meshBounds);
        }
    });

    return combinedBounds.isEmpty()
        ? new THREE.Box3().setFromObject(cabinetRoot)
        : combinedBounds;
};

export const getGroupWidth = (group: THREE.Group<THREE.Object3DEventMap>) => {
    // Placeholders: mesh-only world bounds so overlay children (arrows,
    // measurements) don't inflate width/depth used by clamp + dashed guides.
    const boundingBox = group.userData?.isPlaceholder
        ? computeWorldBoundsExcludingAnimatedDoorParts(group)
        : new THREE.Box3().setFromObject(group);

    let width = boundingBox.max.x - boundingBox.min.x;
    let height = boundingBox.max.y - boundingBox.min.y;
    let depth = boundingBox.max.z - boundingBox.min.z;

    // For placeholders, if bounding box is invalid (NaN or Infinity), use fallback dimensions
    if (group.userData?.isPlaceholder) {
        const isInvalid =
            !isFinite(width) ||
            !isFinite(height) ||
            !isFinite(depth) ||
            width <= 0 ||
            height <= 0 ||
            depth <= 0;

        if (isInvalid) {
            width = 120;
            depth = 120;
            height = 200;
        }
    }

    return {width, height, depth};
};

// Wall name → cabinet rotation (rotation.z) used for wall-mounted products.
export const WALL_ROTATIONS = {
    'wall:back': 0,
    'wall:front': Math.PI,
    'wall:left': Math.PI / 2,
    'wall:right': -Math.PI / 2,
    leftWidthWall: Math.PI / 2,
    leftWidth2Wall: -Math.PI / 2,
    rightWidthWall: 0,
    rightWidth2Wall: Math.PI,
    leftDepthWall: Math.PI,
    rightDepthWall: -Math.PI / 2,
} as const;

export const getDirection = (rotation: number) => {
    const normalizedRotation =
        ((rotation % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);

    if (Math.abs(normalizedRotation) < 0.1) return 'normal';
    if (Math.abs(normalizedRotation - Math.PI / 2) < 0.1) return 'right';
    if (Math.abs(normalizedRotation - Math.PI) < 0.1) return 'back';
    if (Math.abs(normalizedRotation - (3 * Math.PI) / 2) < 0.1) return 'left';
    return 'normal';
};

export type Direction = ReturnType<typeof getDirection>;

// Whether a product faces the front or back wall, so its width runs along x.
export const facesFrontOrBack = (direction: Direction) =>
    direction === 'normal' || direction === 'back';

export const applyBulkheadOffset = (
    x: number,
    z: number,
    cabinet: CabinetMesh,
    direction?: 'normal' | 'right' | 'back' | 'left'
) => {
    const values = cabinet.userData.values;

    const carcassThickness = Number(values?.cabinet_carc_thickness) || 0;
    const materialThickness = Number(values?.cabinet_ext_thickness) || 0;

    const totalThickness = (carcassThickness + materialThickness) * 0.2;
    const halfTotalThickness = totalThickness / 2;

    const flip = direction === 'left' || direction === 'right' ? -1 : 1;

    return {
        x: x + flip * halfTotalThickness,
        z: z - flip * halfTotalThickness,
        thicknessOffset: totalThickness,
    };
};

export interface ActiveFace {
    frontBack: THREE.Vector3;
    leftRight: THREE.Vector3;
}

export const getActiveFaceFromCamera = (
    target: THREE.Group<THREE.Object3DEventMap>,
    camera: THREE.Camera
): ActiveFace | null => {
    const targetWorldPos = new THREE.Vector3();
    target.getWorldPosition(targetWorldPos);

    const camPos = new THREE.Vector3();
    camera.getWorldPosition(camPos);

    // Direction from camera TO target in world space
    const camToTarget = new THREE.Vector3()
        .subVectors(targetWorldPos, camPos)
        .normalize();

    const frontBackZ = camToTarget.z >= 0 ? -1 : 1;
    const leftRightX = camToTarget.x >= 0 ? -1 : 1;

    return {
        frontBack: new THREE.Vector3(0, 0, frontBackZ),
        leftRight: new THREE.Vector3(leftRightX, 0, 0),
    };
};

export const isAdjustableLeg = (object: THREE.Object3D) =>
    (object as THREE.Mesh).isMesh &&
    object.name === ObjectNameMap.adjustableLeg;

export const getAdjustableLegBoxes = (group: THREE.Object3D) => {
    const boxes: THREE.Box3[] = [];

    group.updateWorldMatrix(true, true);
    group.traverse((child) => {
        if (isAdjustableLeg(child)) {
            boxes.push(new THREE.Box3().setFromObject(child));
        }
    });

    return boxes;
};

/**
 * Whether a cabinet stands on adjustable legs. Legs are recognised by mesh
 * name, as the mouse-up overlap check does (useCabinetOverlaps.ts); they load
 * asynchronously, so a cabinet reads as legless until they arrive.
 * @param {THREE.Object3D} group The cabinet.
 * @return {boolean} True when it has at least one leg.
 */
export const hasAdjustableLegs = (group: THREE.Object3D) =>
    getAdjustableLegBoxes(group).length > 0;

/**
 * How far forward a cabinet's legs reach along a direction: the largest
 * projection of any leg's box onto it.
 * @param {THREE.Object3D} group The cabinet.
 * @param {THREE.Vector3} direction A quarter-turn unit vector in the floor
 * plane, normally the cabinet's front normal.
 * @return {number|null} The front-most leg face, or null without legs.
 */
export const getAdjustableLegFrontDepth = (
    group: THREE.Object3D,
    direction: THREE.Vector3
) => {
    const fronts = getAdjustableLegBoxes(group).map((box) =>
        Math.max(box.min.dot(direction), box.max.dot(direction))
    );

    return fronts.length ? Math.max(...fronts) : null;
};

// One face of an axis-aligned box: its outward normal, and its position along
// that normal's axis.
export interface CabinetFace {
    normal: THREE.Vector3;
    position: number;
}

/**
 * Calculates the distance vector between two axis-aligned bounding boxes (AABBs).
 * Each component of the returned Vector3 represents the separation along that axis.
 * If the boxes overlap along an axis, the corresponding component will be 0.
 *
 * @param {THREE.Box3} boxA - The first bounding box.
 * @param {THREE.Box3} boxB - The second bounding box.
 * @return {THREE.Vector3} A vector representing the distance along x, y, and z axes between the boxes.
 */
export function getBoxDistance(
    boxA: THREE.Box3,
    boxB: THREE.Box3
): THREE.Vector3 {
    const dx = Math.max(0, boxA.min.x - boxB.max.x, boxB.min.x - boxA.max.x);
    const dy = Math.max(0, boxA.min.y - boxB.max.y, boxB.min.y - boxA.max.y);
    const dz = Math.max(0, boxA.min.z - boxB.max.z, boxB.min.z - boxA.max.z);
    return new THREE.Vector3(dx, dy, dz);
}

// A horizontal axis of the room plan. Read through the accessors below, so
// one function serves both axes.
export type PlanAxis = 'x' | 'z';

export const along = (vector: THREE.Vector3, axis: PlanAxis) =>
    axis === 'x' ? vector.x : vector.z;
export const minAlong = (box: THREE.Box3, axis: PlanAxis) =>
    axis === 'x' ? box.min.x : box.min.z;
export const maxAlong = (box: THREE.Box3, axis: PlanAxis) =>
    axis === 'x' ? box.max.x : box.max.z;

/**
 * Where the drag target goes along an axis so the dragged box lines up with
 * the other's min edge or its max edge, whichever the box is nearer to.
 * @param {PlanAxis} axis The axis to align along.
 * @param {THREE.Box3} shiftedBox The dragged box at the drag target.
 * @param {THREE.Box3} otherBox The box it aligns to.
 * @param {THREE.Vector3} targetPos The drag target.
 * @return {number} The aligned target along the axis.
 */
export const alignToNearerEdge = (
    axis: PlanAxis,
    shiftedBox: THREE.Box3,
    otherBox: THREE.Box3,
    targetPos: THREE.Vector3
): number => {
    const toMin = Math.abs(
        minAlong(shiftedBox, axis) - minAlong(otherBox, axis)
    );
    const toMax = Math.abs(
        maxAlong(shiftedBox, axis) - maxAlong(otherBox, axis)
    );

    return toMin <= toMax
        ? along(targetPos, axis) +
              (minAlong(otherBox, axis) - minAlong(shiftedBox, axis))
        : along(targetPos, axis) +
              (maxAlong(otherBox, axis) - maxAlong(shiftedBox, axis));
};
